import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectIsLoggedIn, selectIsAuthChecked } from '@/features/auth/redux';
import { checkAuthStatusThunk } from '@/features/auth/redux/thunks/authThunks';
import { getAuthToken } from '@/shared/utils/appCookies';

declare global {
  interface Window {
    $header?: {
      ready: (callback: () => void) => void;
      render: (options?: Record<string, unknown>) => void;
    };
  }
}

const AUTH_POLL_INTERVAL_MS = 1000;
const HEADER_ROOT_ID = 'mmt-header-root';

function setHeaderHeight(height: number) {
  document.documentElement.style.setProperty('--mmt-header-height', `${height}px`);
}

/**
 * Renders the MMT global header only when the user is NOT logged in.
 * Once logged in, the header is hidden and the app's own headers take over.
 */
export function MmtHeader() {
  const dispatch = useAppDispatch();
  const isAuthChecked = useAppSelector(selectIsAuthChecked);
  const isLoggedIn = useAppSelector(selectIsLoggedIn);
  const headerRendered = useRef(false);
  const resizeObserver = useRef<ResizeObserver | null>(null);
  const lastAuthToken = useRef<string | null>(null);

  const shouldShow = isAuthChecked && !isLoggedIn;

  // Render the MMT header once when needed
  useEffect(() => {
    if (!shouldShow || headerRendered.current) return undefined;

    const renderHeader = () => {
      if (headerRendered.current) return;
      headerRendered.current = true;
      window.$header?.render({
        selectedTabName: '',
        activeModal: 'LOGIN',
        forceLogin: true,
        showNav: false,
        onLoginFlowComplete: () => {
          void dispatch(checkAuthStatusThunk());
        },
      });

      requestAnimationFrame(() => {
        const container = document.getElementById(HEADER_ROOT_ID);
        if (!container) return;

        const updateHeight = () => {
          setHeaderHeight(container.getBoundingClientRect().height);
        };
        resizeObserver.current = new ResizeObserver(updateHeight);
        resizeObserver.current.observe(container);
        updateHeight();
      });
    };

    if (window.$header) {
      window.$header.ready(renderHeader);
      return undefined;
    }

    const interval = setInterval(() => {
      if (window.$header) {
        clearInterval(interval);
        window.$header.ready(renderHeader);
      }
    }, 100);
    return () => clearInterval(interval);
  }, [shouldShow, dispatch]);

  // Toggle visibility of the header and all elements header.js injected
  useEffect(() => {
    if (!isAuthChecked) return;

    const container = document.getElementById(HEADER_ROOT_ID);
    if (!container) return;

    if (isLoggedIn) {
      container.style.display = 'none';

      const root = document.getElementById('root');
      let sibling = document.body.firstElementChild;
      while (sibling && sibling !== root) {
        if (sibling instanceof HTMLElement && sibling.id !== HEADER_ROOT_ID) {
          sibling.setAttribute('data-mmt-header-hidden', 'true');
          sibling.style.display = 'none';
        }
        sibling = sibling.nextElementSibling;
      }

      setHeaderHeight(0);
      resizeObserver.current?.disconnect();
    } else if (headerRendered.current) {
      container.style.display = '';

      document.querySelectorAll('[data-mmt-header-hidden]').forEach((el) => {
        if (el instanceof HTMLElement) {
          el.style.display = '';
          el.removeAttribute('data-mmt-header-hidden');
        }
      });

      const height = container.getBoundingClientRect().height;
      setHeaderHeight(height);
    }
  }, [isAuthChecked, isLoggedIn]);

  // Poll mmt-auth cookie to detect login/logout from the external header
  useEffect(() => {
    if (!isAuthChecked) return;

    lastAuthToken.current = getAuthToken();

    const interval = setInterval(() => {
      const currentToken = getAuthToken();
      const hadToken = lastAuthToken.current !== null && lastAuthToken.current !== '';
      const hasToken = currentToken !== null && currentToken !== '';

      if (hadToken !== hasToken) {
        lastAuthToken.current = currentToken;
        void dispatch(checkAuthStatusThunk());
      }
    }, AUTH_POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [dispatch, isAuthChecked, isLoggedIn]);

  return null;
}
